import React, { memo } from 'react';
import { Table } from 'antd';
import moment from 'moment';
import Loader from '@components/Loader';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { AssetItemDto } from '@api/Api';
import UserAvatar from '@components/UserAvatar';
import Status from '@components/Approval/Status';
import { FolderWithItems } from '@pages/Campaigns/CampaignReview/utils';
import ActionFolder from '@pages/Campaigns/CampaignReview/components/Actions/ActionFolder';
import ActionAsset from '@pages/Campaigns/CampaignReview/components/Actions/ActionAsset';
import { FolderIcon, FolderPrivateIcon } from '@icons';
import './ListView.less';

type ListViewProps = {
  onSelectFolder: (folderId: string) => void;
  folders: FolderWithItems[];
  assetsList: AssetItemDto[];
  canManageReviews: boolean;
  reviewId: string;
  onOpenAsset: (asset: AssetItemDto) => void;
};

export default memo(function ListView({
  onSelectFolder,
  folders,
  assetsList,
  onOpenAsset,
  canManageReviews,
  reviewId
}: ListViewProps) {
  const foldersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 380,
      render: (name: string, { id, isPrivate }: FolderWithItems) => {
        return (
          <div className="campaign-review-list-view-folder">
            <div
              className="list-view-folder-icon"
              onClick={() => {
                onSelectFolder(id);
              }}
            >
              {isPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
            </div>
            {name}
          </div>
        );
      }
    },
    {
      title: ' ',
      dataIndex: 'owner',
      key: 'owner',
      width: 190
    },
    {
      title: ' ',
      dataIndex: 'created',
      key: 'created',
      width: 190
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (_id: string, folder: FolderWithItems) => {
        const modified = Math.max(
          ...folder.items.map((x) => new Date(x.modifiedAt).getTime())
        );
        return moment(modified).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, folder: FolderWithItems) => {
        const assetsToDownload = folder.items.filter(
          (asset) => !!asset.asset?.permissions.download
        );

        return (
          <ActionFolder
            canManageReviews={canManageReviews}
            assets={assetsToDownload}
            folderName={folder.name}
            reviewId={reviewId}
            folderId={folder.id}
          />
        );
      }
    }
  ];

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 380,
      render: (_name: string, { asset }: AssetItemDto) => {
        const preparePreview = assetPreviewThumbnails({
          url: asset?.versions[0].metadata.preview?.url || '',
          status: asset?.versions[0].metadata.preview?.status || 'ready',
          type: asset?.versions[0].metadata.type || '',
          size: 40,
          format: 'img'
        });
        return (
          <div className="campaign-review-list-view-asset">
            <div className="campaign-review-list-view-asset-icon">
              {preparePreview === 'pending' && (
                <Loader spinSize={40} isSpinning={true} />
              )}
              {preparePreview !== 'pending' &&
              typeof preparePreview === 'string' ? (
                <img src={preparePreview || ''} alt="" />
              ) : (
                preparePreview
              )}
            </div>
            <div className="campaign-review-list-view-asset-name">
              {asset?.versions?.[0]?.name}.{asset?.versions?.[0]?.extension}
              {!!asset?.approvalWorkflow && (
                <Status
                  showOverdueTag
                  status={asset.approvalWorkflow.status}
                  approvalWorkflowStatus={asset.approvalWorkflow.status}
                  approvalWorkflowIsPaused={asset.approvalWorkflow.isPaused}
                  deadline={asset.approvalWorkflow.deadline}
                />
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 190,
      render: (_id: string, { owner }: AssetItemDto) => {
        return (
          <UserAvatar
            isActive={true}
            size={40}
            src={owner?.user?.picture.url || ''}
            userEmail={owner?.user?.email || ''}
            userName={owner?.user?.name || ''}
          />
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      render: (createdAt: string) => {
        return moment(createdAt).format('MMM DD, YYYY');
      }
    },
    {
      title: 'Modified',
      key: 'modifiedAt',
      dataIndex: 'modifiedAt',
      width: 160,
      render: (modifiedAt: string) => {
        return moment(modifiedAt).fromNow();
      }
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'id',
      width: 32,
      render: (_id: string, asset: AssetItemDto) => {
        return (
          <ActionAsset
            canManageReviews={canManageReviews}
            asset={asset}
            reviewId={reviewId}
          />
        );
      }
    }
  ];

  return (
    <div className="campaign-review-list-view">
      {!!folders?.length && (
        <div className="campaign-review-list-view-folders">
          <div className="campaign-review-list-view-label">Folders</div>
          <Table
            pagination={false}
            columns={foldersColumns}
            dataSource={folders}
            tableLayout="fixed"
            onRow={({ id }) => ({
              onClick: () => {
                onSelectFolder(id);
              }
            })}
          />
        </div>
      )}
      {!!assetsList?.length && (
        <div className="campaign-review-list-view-assets">
          <div className="campaign-review-list-view-label">Assets</div>
          <Table
            pagination={false}
            columns={columns}
            dataSource={assetsList}
            rowKey={({ id }) => id}
            tableLayout="fixed"
            onRow={(asset) => ({
              onClick: () => {
                onOpenAsset(asset);
              }
            })}
          />
        </div>
      )}
    </div>
  );
});
